import React from 'react'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { helper } from '../../../utilities/helper'
import {
  formatPortfolioDate,
  getEventServices,
  getEventType,
  getEventVenue,
} from '../../../utilities/portfolioPresentation'
import Container from '../foundations/Container'
import Action from '../primitives/Action'
import Media from '../primitives/Media'
import { MEDIA_POLICY_KEYS } from '../primitives/mediaPolicy'
import useRevealOnce from '../motion/useRevealOnce'
import motionStyles from '../motion/MotionReveal.module.scss'
import styles from './EventShowcase.module.scss'

const plainText = value => String(value || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const EventShowcase = ({
  actionLabel = 'View Production Portfolio',
  containerWidth = 'standard',
  description,
  events = [],
  filter = [],
  heading = 'Sound in real event environments',
  eyebrow = 'Recent production work',
  sectionId = 'recent-speaker-events',
  totalEvents,
  motionProfile = 'static',
  layout = 'grid',
}) => {
  const shouldAnimate = motionProfile === 'homepage'
  const { entered, ref } = useRevealOnce({ enabled: shouldAnimate })
  const visibleEvents = [...events]
    .filter(event => !filter.length || filter.includes(event.category))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3)

  if (!visibleEvents.length) return null

  return (
    <section className={styles.section} aria-labelledby={sectionId}>
      <Container width={containerWidth}>
        <div className={styles.heading}>
          <div>
            {eyebrow && <span>{eyebrow}</span>}
            <h2 id={sectionId}>{heading}</h2>
            {description && <p className={styles.description}>{description}</p>}
          </div>
          <div className={styles.proof}>
            {totalEvents && <strong>{totalEvents}</strong>}
            <span>{totalEvents ? `events serviced in ${new Date().getFullYear()}` : 'Real Miami Sound Rental events'}</span>
          </div>
        </div>

        <div
          ref={ref}
          className={`${styles.grid} ${layout === 'featured' ? styles.featuredGrid : ''} ${shouldAnimate ? motionStyles.stagger : ''} ${entered ? motionStyles.entered : ''}`.trim()}
        >
          {visibleEvents.map(event => {
            const eventType = getEventType(event)
            const venue = getEventVenue(event)
            const services = getEventServices(event).slice(0, 3)
            const scope = helper.shortText(plainText(
              event.subtitle || event.main_paragraph || event.second_paragraph
            ), 18)
            const meta = [eventType, venue].filter(Boolean).join(' · ')

            return (
              <Action
                href={`/portfolio/${helper.makeUrl(event.title)}`}
                key={event._id}
                variant='plain'
                wrapContent={false}
                className={styles.card}
              >
                <Media
                  picture={event.pictures?.[0]}
                  alt={`${event.title} event produced by Miami Sound Rental`}
                  policy={MEDIA_POLICY_KEYS.CARD}
                  ratio='4 / 3'
                />
                <span className={styles.cardCopy}>
                  <small className={styles.cardMeta}>{meta}</small>
                  <strong>{event.title}</strong>
                  {scope && <span className={styles.cardScope}>{scope}</span>}
                  <span className={styles.cardFooter}>
                    <span className={styles.serviceList}>{services.join(' · ')}</span>
                    <small>{formatPortfolioDate(event.date)}</small>
                  </span>
                </span>
              </Action>
            )
          })}
        </div>

        <div className={styles.action}>
          <Action href='/portfolio' variant='secondary' icon={FiArrowRight}>
            {actionLabel}
          </Action>
        </div>
      </Container>
    </section>
  )
}

export default EventShowcase
